import { useAuth } from '../context/AuthContext';
import { useState, useEffect } from 'react';

/**
 * Hook to check if financial information should be hidden
 * Returns true if:
 * 1. User's own hideFinancials is true, OR
 * 2. User is staff/manager/stakeholder AND admin has hideFinancialsFromStaff enabled
 * @returns {boolean} - true if financials should be hidden, false otherwise
 */
export const useFinancialVisibility = () => {
  const { currentUser } = useAuth();
  const [adminHidesFromStaff, setAdminHidesFromStaff] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch admin settings if user is not an admin
    const fetchAdminSettings = async () => {
      if (!currentUser) {
        setLoading(false);
        return;
      }
      
      // If user is admin/master, they control their own visibility
      if (currentUser.role === 'admin' || currentUser.isMaster || currentUser.role === 'master_panel') {
        // Admin's own setting for hiding from staff
        setAdminHidesFromStaff(false);
        setLoading(false);
        return;
      }
      
      // For non-admin users, check if their admin has hidden financials
      try {
        const token = localStorage.getItem('token');
        const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
        
        const response = await fetch(`${backendUrl}/api/auth/admin-settings`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          setAdminHidesFromStaff(data.hideFinancialsFromStaff === true);
        }
      } catch (error) {
        console.error('Failed to fetch admin settings:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAdminSettings();
  }, [currentUser]);

  // Return true if user's own setting OR admin's staff setting is enabled
  if (currentUser?.hideFinancials === true) {
    return true;
  }
  
  // For non-admin users, check admin's setting
  if (currentUser && currentUser.role !== 'admin' && !currentUser.isMaster && currentUser.role !== 'master_panel') {
    return adminHidesFromStaff;
  }
  
  return false;
};

/**
 * Extended hook that also returns whether admin has hidden financials from staff
 * Useful for conditionally showing/hiding the financial toggle option in profile
 */
export const useFinancialVisibilityExtended = () => {
  const { currentUser } = useAuth();
  const [adminHidesFromStaff, setAdminHidesFromStaff] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAdminSettings = async () => {
      if (!currentUser) {
        setLoading(false);
        return;
      }
      
      // If user is admin/master, they control their own visibility
      if (currentUser.role === 'admin' || currentUser.isMaster || currentUser.role === 'master_panel') {
        setAdminHidesFromStaff(false);
        setLoading(false);
        return;
      }
      
      // For non-admin users, check if their admin has hidden financials
      try {
        const token = localStorage.getItem('token');
        const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
        
        const response = await fetch(`${backendUrl}/api/auth/admin-settings`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          setAdminHidesFromStaff(data.hideFinancialsFromStaff === true);
        }
      } catch (error) {
        console.error('Failed to fetch admin settings:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAdminSettings();
  }, [currentUser]);

  // Calculate if financials should be hidden
  let hideFinancials = false;
  
  if (currentUser?.hideFinancials === true) {
    hideFinancials = true;
  } else if (currentUser && currentUser.role !== 'admin' && !currentUser.isMaster && currentUser.role !== 'master_panel') {
    hideFinancials = adminHidesFromStaff;
  }
  
  return {
    hideFinancials,
    adminHidesFromStaff,
    loading,
    isStaffUser: currentUser && ['staff', 'manager', 'stakeholder', 'receptionist'].includes(currentUser.role)
  };
};

/**
 * Helper component to conditionally hide financial information
 */
export const HideFinancials = ({ children, fallback = '***' }) => {
  const hideFinancials = useFinancialVisibility();
  
  if (hideFinancials) {
    return fallback;
  }
  
  return children;
};

export default useFinancialVisibility;
